import { supabase } from './supabaseClient'
import {
  hasProAccess,
  hasProPlusAccess,
  kiloPlanToCurrentTier,
  normalizeKiloPlan,
  resolveEntitledPlan,
  type KiloPlan,
} from './planTier'

export type SubscriptionStatus = 'free' | 'premium' | 'trialing' | 'expired'

export type UserTierState = {
  subscription_status: SubscriptionStatus
  subscription_start_date: string | null
  subscription_end_date: string | null
  calendar_sync_enabled: boolean
  current_tier: string
  trial_end_date: string | null
  stripe_customer_id: string | null
  plan: KiloPlan
}

export function normalizeSubscriptionStatus(raw: string | null | undefined): SubscriptionStatus {
  const value = (raw ?? 'free').toLowerCase()
  if (value === 'premium' || value === 'trialing' || value === 'expired' || value === 'free') {
    return value
  }
  if (value === 'active' || value === 'past_due') return 'premium'
  return 'free'
}

function effectiveStatus(status: SubscriptionStatus, trialEndDate: string | null): SubscriptionStatus {
  if (status !== 'trialing' || !trialEndDate) return status
  const today = new Date().toISOString().slice(0, 10)
  return trialEndDate < today ? 'free' : status
}

export async function loadUserTier(userId: string): Promise<{ tier: UserTierState | null; error: string | null }> {
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('subscription_status, subscription_start_date, subscription_end_date, calendar_sync_enabled')
    .eq('id', userId)
    .single()

  if (profileError) {
    return { tier: null, error: profileError.message }
  }

  const { data: userTier } = await supabase
    .from('user_tier')
    .select('current_tier, trial_end_date, subscription_status')
    .eq('user_id', userId)
    .maybeSingle()
  const { data: subscriptionRow } = await supabase
    .from('subscriptions')
    .select('stripe_customer_id, plan_id')
    .eq('user_id', userId)
    .maybeSingle()

  const trialEndDate = userTier?.trial_end_date ?? profile.subscription_end_date ?? null
  const status = effectiveStatus(
    normalizeSubscriptionStatus(profile.subscription_status ?? userTier?.subscription_status),
    trialEndDate,
  )
  const plan = resolveEntitledPlan(
    subscriptionRow?.plan_id ?? userTier?.current_tier,
    status,
  )

  return {
    tier: {
      subscription_status: status,
      subscription_start_date: profile.subscription_start_date,
      subscription_end_date: profile.subscription_end_date,
      calendar_sync_enabled: Boolean(profile.calendar_sync_enabled),
      current_tier: kiloPlanToCurrentTier(plan),
      trial_end_date: trialEndDate,
      stripe_customer_id: subscriptionRow?.stripe_customer_id ?? null,
      plan,
    },
    error: null,
  }
}

export async function setSubscriptionStatus(
  userId: string,
  status: SubscriptionStatus,
  options?: { trialDays?: number },
): Promise<{ error: string | null }> {
  const now = new Date()
  const start = now.toISOString().slice(0, 10)
  let end: string | null = null

  if (status === 'trialing' && options?.trialDays) {
    const trialEnd = new Date(now)
    trialEnd.setDate(trialEnd.getDate() + options.trialDays)
    end = trialEnd.toISOString().slice(0, 10)
  }

  const profileUpdate = {
    subscription_status: status,
    subscription_start_date: start,
    subscription_end_date: end,
    paywall_dismissed_at: new Date().toISOString(),
  }

  const { error: profileError } = await supabase.from('profiles').update(profileUpdate).eq('id', userId)
  if (profileError) return { error: profileError.message }

  const tierPayload = {
    user_id: userId,
    current_tier: 'FREE' as const,
    subscription_status: status,
    trial_end_date: end,
  }

  const { error: tierError } = await supabase.from('user_tier').upsert(tierPayload, { onConflict: 'user_id' })
  if (tierError) return { error: tierError.message }

  const { error: subError } = await supabase.from('subscriptions').upsert(
    {
      user_id: userId,
      status,
      plan_id: 'free',
      trial_end_date: end,
      current_period_start: start,
      current_period_end: end,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' },
  )

  if (subError) return { error: subError.message }

  return { error: null }
}

export function isPremiumTier(tier: UserTierState | null): boolean {
  return hasProAccess(tier?.plan ?? 'free')
}

export function isProTier(tier: UserTierState | null): boolean {
  return hasProAccess(tier?.plan ?? 'free')
}

export function isProPlusTier(tier: UserTierState | null): boolean {
  return hasProPlusAccess(tier?.plan ?? 'free')
}

export function planLabelKey(plan: KiloPlan): 'profile.free' | 'profile.pro' | 'profile.proPlus' {
  if (plan === 'pro_plus') return 'profile.proPlus'
  if (plan === 'pro') return 'profile.pro'
  return 'profile.free'
}

export { normalizeKiloPlan, type KiloPlan }
